import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Leaf, Heart, Wheat, Waves } from 'lucide-react';
import { usePreloadImage } from '../hooks/usePreloadImage';
import { useOnlineOrderingEnabled } from '../hooks/usePricingConfig';
import heroSunset from '../assets/hero-sunset.webp';
import productLifestyle from '../assets/product-lifestyle.webp';

const highlights = [
  { Icon: Leaf, title: 'Simple Ingredients', desc: 'We use only real, whole ingredients you can feel good about.' },
  { Icon: Wheat, title: 'Small-Batch', desc: 'Made in small batches for the perfect crunch and flavor in every bite.' },
  { Icon: Heart, title: 'No Refined Sugar', desc: 'Naturally sweetened options that let the ingredients shine.' },
  { Icon: Waves, title: 'Coastal Inspired', desc: 'Inspired by the coast and crafted with care for everyday living.' },
] as const;

export function HomePage() {
  usePreloadImage(heroSunset);
  // While online ordering is off (or the config is still loading) the CTAs say
  // "view" rather than "shop" — products can be browsed but not bought online.
  const orderingEnabled = useOnlineOrderingEnabled() === true;
  return (
    <div>
      <section className="relative overflow-hidden bg-coastal-800">
        <img
          src={heroSunset}
          alt="Sunset over the coast"
          decoding="sync"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/50 to-transparent" />
        <div className="container-max relative z-10 flex items-start min-h-[60vh] md:min-h-[88vh]">
          <div className="max-w-2xl pt-36 md:pt-44 pb-16 md:pb-20">
            <h1 className="font-serif font-bold text-white text-5xl md:text-6xl leading-[1.05] drop-shadow-lg">
              <span className="block sm:whitespace-nowrap">Simple ingredients.</span>
              <span className="block italic font-normal text-white/90 mt-1">Thoughtfully crafted.</span>
            </h1>
            <div className="w-24 h-px bg-white/50 my-7" />
            <p className="text-lg md:text-xl text-white/90 mb-9 max-w-md drop-shadow">
              Small-batch Seed &amp; Nut Crisps and Oat &amp; Seed Crisps made with no refined sugar.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/products">
                <Button size="lg">{orderingEnabled ? 'Shop Now' : 'View Products'}</Button>
              </Link>
              <Link to="/about">
                <Button variant="white" size="lg">Our Story</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-cream">
        <div className="container-max text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-coastal-600">
            Good ingredients. Honest snacks.
          </h2>
          <div className="flex items-center justify-center gap-3 mt-4 mb-12">
            <span className="h-px w-12 bg-gold-400" />
            <Waves className="w-6 h-6 text-gold-500" />
            <span className="h-px w-12 bg-gold-400" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
            {highlights.map(({ Icon, title, desc }) => (
              <div key={title} className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full border border-gold-300 flex items-center justify-center mb-4 text-coastal-500">
                  <Icon className="w-7 h-7" />
                </div>
                <h3 className="font-serif font-bold text-coastal-600 mb-2">{title}</h3>
                <p className="text-sm text-gray-600 max-w-[14rem]">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container-max">
          <div className="relative rounded-2xl overflow-hidden shadow-xl">
            <img
              src={productLifestyle}
              alt="Coastal Harvest product collection"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            <div className="absolute inset-0 flex flex-col items-center justify-end text-center p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-white drop-shadow-lg mb-3">
                Explore the Collection
              </h2>
              <p className="text-white/90 max-w-xl mb-6 drop-shadow">
                Browse our full range of small-batch crunches, granolas, and blends.
              </p>
              <Link to="/products">
                <Button size="lg">{orderingEnabled ? 'Shop All Products' : 'View All Products'}</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
